import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import FileUpload from '../components/FileUpload';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Package,
  Tag,
  Eye,
  EyeOff,
  Grid,
  List,
  MoreVertical,
  Image as ImageIcon,
  DollarSign,
  Star,
  Clock,
  Hash,
  X,
  Group,
  Link,
  Unlink,
} from 'lucide-react';

const MenuItems = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterAvailability, setFilterAvailability] = useState('all'); // all, available, unavailable, deleted
  const [sortBy, setSortBy] = useState('name'); // name, price, created_at
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedItemForAddons, setSelectedItemForAddons] = useState(null);
  // Separate state for modal menu selection (independent from filter)
  const [modalSelectedMenu, setModalSelectedMenu] = useState('');
  const [modalSelectedCategory, setModalSelectedCategory] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const queryClient = useQueryClient();

  // Check connection status
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await api.get('/ping');
        setConnectionStatus('connected');
      } catch (error) {
        setConnectionStatus('disconnected');
      }
    };
    
    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Handle editing item state changes
  useEffect(() => {
    if (editingItem) {
      console.log('🔄 Setting modal state for editing item:', editingItem.name);
      console.log('📋 Item menu_id:', editingItem.menu_id, 'category_id:', editingItem.category_id);
      setModalSelectedMenu(editingItem.menu_id || '');
      setModalSelectedCategory(editingItem.category_id || '');
    }
  }, [editingItem]);

  const { data: menuItems, isLoading, error: menuItemsError } = useQuery(
    ['menu-items', selectedMenu, selectedCategory, sortBy, filterAvailability],
    async () => {
      const params = {};
      if (selectedMenu) params.menu_id = selectedMenu;
      if (selectedCategory) params.category_id = selectedCategory;
      if (sortBy) params.sort_by = sortBy;
      if (filterAvailability === 'deleted') params.include_deleted = 'true';
      console.log('🔍 Fetching menu items with params:', params);
      const response = await api.get('/vendor/menu-items', { params });
      console.log('📊 Menu items response:', response.data.length, 'items');
      console.log('📋 Items with deleted_at:', response.data.filter(item => item.deleted_at).length);
      
      // Debug: Check first item structure for category data
      if (response.data.length > 0) {
        const firstItem = response.data[0];
        console.log('🔍 First item structure:', {
          id: firstItem.id,
          name: firstItem.name,
          category_id: firstItem.category_id,
          menuCategory: firstItem.menuCategory,
          category: firstItem.category
        });
      }
      
      return response.data;
    },
    {
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error('❌ Failed to fetch menu items:', error);
        showErrorToast('Failed to load menu items. Please try again.', 'menu-items-load');
      }
    }
  );

  const { data: menus } = useQuery(
    ['menus'],
    async () => {
      const response = await api.get('/vendor/menus');
      return response.data;
    }
  );

  // Categories for filter dropdown
  const { data: categories } = useQuery(
    ['menu-categories-filter', selectedMenu],
    async () => {
      if (!selectedMenu) return [];
      console.log('🔍 Loading filter categories for menu ID:', selectedMenu);
      const response = await api.get(`/vendor/menus/${selectedMenu}/categories`);
      console.log('📋 Filter categories loaded:', response.data.length, 'categories');
      return response.data;
    },
    { enabled: !!selectedMenu }
  );

  // Categories for modal dropdown (separate from filter)
  const { data: modalCategories, isLoading: modalCategoriesLoading } = useQuery(
    ['menu-categories-modal', modalSelectedMenu],
    async () => {
      if (!modalSelectedMenu) return [];
      console.log('🔍 Loading modal categories for menu ID:', modalSelectedMenu);
      const response = await api.get(`/vendor/menus/${modalSelectedMenu}/categories`);
      console.log('📋 Modal categories loaded:', response.data.length, 'categories');
      return response.data;
    },
    { 
      enabled: !!modalSelectedMenu,
      retry: 2,
      retryDelay: 500,
      onError: (error) => {
        console.error('❌ Failed to load modal categories:', error);
        showErrorToast('Failed to load categories. Please try again.', 'modal-categories-load');
      }
    }
  );

  // Addon Groups API Queries - Vendor specific
  const { data: addonGroups, isLoading: addonGroupsLoading, error: addonGroupsError } = useQuery(
    ['vendor-addon-groups'],
    async () => {
      console.log('🔍 Fetching vendor addon groups...');
      const response = await api.get('/vendor/addon-groups');
      console.log('📊 Vendor addon groups response:', response.data);
      return response.data.addonGroups || [];
    },
    {
      retry: 3,
      retryDelay: 1000,
      onError: (error) => {
        console.error('❌ Failed to fetch vendor addon groups:', error);
      }
    }
  );

  const { data: itemAddonGroups } = useQuery(
    ['item-addon-groups', selectedItemForAddons?.id],
    async () => {
      if (!selectedItemForAddons?.id) return [];
      const response = await api.get(`/api/menu-items/${selectedItemForAddons.id}/addon-groups`);
      return response.data.addonGroups || [];
    },
    { enabled: !!selectedItemForAddons?.id }
  );

  const createItemMutation = useMutation(
    async (itemData) => {
      const response = await api.post('/vendor/menu-items', itemData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-items']);
        showSuccessToast('Menu item created successfully!', 'item-create');
        setShowCreateModal(false);
        setModalSelectedMenu(''); // Reset modal state
        setModalSelectedCategory(''); // Reset category state
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to create menu item', 'item-create');
      },
    }
  );

  const updateItemMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/vendor/menu-items/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-items']);
        showSuccessToast('Menu item updated successfully!', 'item-update');
        setEditingItem(null);
        setModalSelectedMenu(''); // Reset modal state
        setModalSelectedCategory(''); // Reset category state
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update menu item', 'item-update');
      },
    }
  );

  const deleteItemMutation = useMutation(
    async (id) => {
      console.log('🗑️ Attempting to soft delete menu item with ID:', id);
      console.log('🔗 API URL:', `/vendor/menu-items/${id}`);
      console.log('🔑 Auth token:', localStorage.getItem('vendorToken') ? 'Present' : 'Missing');
      
      try {
        const response = await api.delete(`/vendor/menu-items/${id}`);
        console.log('✅ Soft delete response:', response);
        return response;
      } catch (error) {
        console.error('❌ Soft delete error:', error);
        console.error('❌ Error response:', error.response);
        console.error('❌ Error status:', error.response?.status);
        console.error('❌ Error data:', error.response?.data);
        throw error;
      }
    },
    {
      onSuccess: (data, variables) => {
        console.log('🎉 Soft delete mutation succeeded for item ID:', variables);
        // Invalidate all menu-items queries to ensure fresh data
        queryClient.invalidateQueries(['menu-items']);
        // Also force a refetch of the current query
        queryClient.refetchQueries(['menu-items', selectedMenu, selectedCategory, sortBy, filterAvailability]);
        
        // Show appropriate success message based on response
        const message = data?.data?.message || 'Menu item soft deleted successfully!';
        showSuccessToast(message, 'item-delete');
      },
      onError: (error, variables) => {
        console.error('💥 Soft delete mutation failed for item ID:', variables, 'Error:', error);
        const errorMessage = error.response?.data?.message || 'Failed to delete menu item';
        showErrorToast(errorMessage, 'item-delete');
      },
    }
  );

  const restoreItemMutation = useMutation(
    async (id) => {
      await api.patch(`/vendor/menu-items/${id}/restore`);
    },
    {
      onSuccess: () => {
        // Invalidate all menu-items queries to ensure fresh data
        queryClient.invalidateQueries(['menu-items']);
        // Also force a refetch of the current query
        queryClient.refetchQueries(['menu-items', selectedMenu, selectedCategory, sortBy, filterAvailability]);
        showSuccessToast('Menu item restored successfully!', 'item-restore');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to restore menu item', 'item-restore');
      },
    }
  );

  const toggleAvailabilityMutation = useMutation(
    async (id) => {
      await api.patch(`/vendor/menu-items/${id}/toggle`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-items']);
        showSuccessToast('Item availability updated!', 'item-availability');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update availability', 'item-availability');
      },
    }
  );

  // Addon Group Mutations
  const mapAddonGroupMutation = useMutation(
    async ({ itemId, groupId, sortOrder }) => {
      const response = await api.post(`/api/menu-items/${itemId}/addon-groups/${groupId}`, {
        sort_order: sortOrder
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['item-addon-groups']);
        queryClient.invalidateQueries(['vendor-addon-groups']);
        showSuccessToast('Addon group mapped successfully!');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to map addon group');
      },
    }
  );

  const unmapAddonGroupMutation = useMutation(
    async ({ itemId, groupId }) => {
      const response = await api.delete(`/api/menu-items/${itemId}/addon-groups/${groupId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['item-addon-groups']);
        queryClient.invalidateQueries(['vendor-addon-groups']);
        showSuccessToast('Addon group unmapped successfully!');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to unmap addon group');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Use modalSelectedMenu for menu_id when editing, fallback to form data
    const menuId = editingItem ? modalSelectedMenu : formData.get('menu_id');
    const name = formData.get('name');
    const price = formData.get('price');
    
    if (!menuId) {
      showErrorToast('Please select a menu', 'form-validation');
      return;
    }
    
    if (!name || name.trim() === '') {
      showErrorToast('Please enter an item name', 'form-validation');
      return;
    }
    
    if (!price || parseFloat(price) <= 0) {
      showErrorToast('Please enter a valid price', 'form-validation');
      return;
    }
    
    // Use modalSelectedCategory for category_id when editing, fallback to form data
    const categoryId = editingItem ? modalSelectedCategory : formData.get('category_id');
    
    // Validate that if a category is selected, it belongs to the selected menu
    if (categoryId && categoryId !== '') {
      const selectedCategory = modalCategories?.find(cat => cat.id == categoryId);
      if (!selectedCategory) {
        showErrorToast('Selected category is not valid for the chosen menu', 'form-validation');
        return;
      }
    }
    
    const itemData = {
      menu_id: menuId,
      category_id: categoryId && categoryId !== '' ? categoryId : null,
      name: name.trim(),
      description: formData.get('description')?.trim() || '',
      price: parseFloat(price),
      image_url: formData.get('image_url') || null,
      is_available: formData.get('is_available') === 'true',
    };

    console.log('📝 Submitting item data:', itemData);
    console.log('📋 Modal state - Menu:', modalSelectedMenu, 'Category:', modalSelectedCategory);

    if (editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data: itemData });
    } else {
      createItemMutation.mutate(itemData);
    }
  };

  const handleMenuChange = (menuId) => {
    // Update modalSelectedMenu when menu changes in modal (independent from filter)
    setModalSelectedMenu(menuId);
    // Reset category when menu changes (since categories are menu-specific)
    setModalSelectedCategory('');
    // Invalidate categories query to ensure fresh data for the new menu
    if (menuId) {
      queryClient.invalidateQueries(['menu-categories-modal']);
    }
    console.log('🔄 Menu changed to:', menuId, 'Category reset');
    console.log('📋 Modal state after menu change - Menu:', menuId, 'Category: (reset)');
  };

  const handleCategoryChange = (categoryId) => {
    setModalSelectedCategory(categoryId);
    console.log('🏷️ Category changed to:', categoryId);
    console.log('📋 Modal state after category change - Menu:', modalSelectedMenu, 'Category:', categoryId);
  };

  const handleManageAddons = (item) => {
    setSelectedItemForAddons(item);
    setShowAddonModal(true);
  };

  const filteredItems = menuItems?.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAvailability = filterAvailability === 'all' || 
      (filterAvailability === 'available' && item.is_available && !item.deleted_at) ||
      (filterAvailability === 'unavailable' && !item.is_available && !item.deleted_at) ||
      (filterAvailability === 'deleted' && item.deleted_at);
    return matchesSearch && matchesAvailability;
  }) || [];

  const MenuItemModal = ({ item = null, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {item ? 'Edit Menu Item' : 'Create New Menu Item'}
          </h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Menu *
              </label>
              <select
                name="menu_id"
                value={modalSelectedMenu}
                onChange={(e) => handleMenuChange(e.target.value)}
                required
                className="input-field"
              >
                <option value="">Select a menu</option>
                {menus?.map((menu) => (
                  <option key={menu.id} value={menu.id}>
                    {menu.type} Menu
                  </option>
                ))}
              </select>
              {modalSelectedMenu && modalCategoriesLoading && (
                <div className="text-xs text-blue-600 mt-1">
                  Loading categories for selected menu...
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Category
              </label>
              <select
                name="category_id"
                value={modalSelectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="input-field"
                disabled={modalCategoriesLoading}
              >
                <option value="">No category</option>
                {modalCategoriesLoading ? (
                  <option value="" disabled>Loading categories...</option>
                ) : modalCategories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {modalCategoriesLoading ? (
                <div className="text-xs text-secondary-500 mt-1 flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-500 mr-1"></div>
                  Loading categories...
                </div>
              ) : modalCategories && (
                <div className="text-xs text-secondary-500 mt-1">
                  {modalCategories.length} categories available
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Item Name *
            </label>
            <input
              type="text"
              name="name"
              defaultValue={item?.name || ''}
              required
              className="input-field"
              placeholder="e.g., Margherita Pizza"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              defaultValue={item?.description || ''}
              rows="3"
              className="input-field"
              placeholder="Describe your dish..."
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Price *
              </label>
              <div className="relative">
                <DollarSign className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="number"
                  name="price"
                  defaultValue={item?.price || ''}
                  min="0"
                  step="0.01"
                  required
                  className="input-field pl-10"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Item Image
              </label>
              <FileUpload
                onUploadSuccess={(file) => {
                  // Update the hidden input with the uploaded file URL
                  const imageUrlInput = document.querySelector('input[name="image_url"]');
                  if (imageUrlInput) {
                    imageUrlInput.value = file.url;
                  }
                }}
                onUploadError={(error) => {
                  console.error('Upload error:', error);
                }}
                multiple={false}
                placeholder="Upload item image"
                showPreview={true}
              />
              <input
                type="hidden"
                name="image_url"
                defaultValue={item?.image_url || ''}
              />
            </div>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_available"
                value="true"
                defaultChecked={item?.is_available ?? true}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">Available for ordering</span>
            </label>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={createItemMutation.isLoading || updateItemMutation.isLoading}
            >
              {createItemMutation.isLoading || updateItemMutation.isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                item ? 'Update Item' : 'Create Item'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Addon Management Modal Component
  const AddonManagementModal = ({ item, onClose }) => {
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [sortOrder, setSortOrder] = useState(1);

    const handleMapGroup = () => {
      if (!selectedGroup) return;
      mapAddonGroupMutation.mutate({
        itemId: item.id,
        groupId: selectedGroup,
        sortOrder: sortOrder
      }, {
        onSuccess: () => {
          // Clear selection after successful mapping
          setSelectedGroup(null);
          setSortOrder(1);
          
          // Show success feedback and scroll to mapped groups
          setTimeout(() => {
            const mappedSection = document.querySelector('[data-mapped-groups]');
            if (mappedSection) {
              mappedSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 500);
        }
      });
    };

    const handleUnmapGroup = (groupId) => {
      unmapAddonGroupMutation.mutate({
        itemId: item.id,
        groupId: groupId
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Manage Addons for {item.name}</h2>
            <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Debug Information */}
          {/* <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
            <strong>🐛 Debug Info:</strong>
            <div>Vendor Addon Groups Loading: {addonGroupsLoading ? 'Yes' : 'No'}</div>
            <div>Vendor Addon Groups Error: {addonGroupsError ? addonGroupsError.message : 'None'}</div>
            <div>Vendor Addon Groups Data: {addonGroups ? `${addonGroups.length} groups loaded` : 'No data'}</div>
            <div>Item Addon Groups: {itemAddonGroups ? `${itemAddonGroups.length} mapped` : 'No data'}</div>
          </div> */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Addon Groups */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Available Vendor Addon Groups</h3>
              <div className="text-sm text-blue-600 mb-4 p-2 bg-blue-50 rounded border border-blue-200">
                💡 <strong>Step 1:</strong> Click the [🔗] button to select a vendor addon group, then complete mapping below
              </div>
              <div className="space-y-2">
                {addonGroupsLoading ? (
                  <div className="p-4 text-center text-secondary-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    Loading vendor addon groups...
                  </div>
                ) : addonGroupsError ? (
                  <div className="p-4 text-center text-red-500">
                    <p>❌ Error loading vendor addon groups:</p>
                    <p className="text-sm">{addonGroupsError.message}</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      🔄 Retry
                    </button>
                  </div>
                ) : Array.isArray(addonGroups) && addonGroups.length > 0 ? (
                  <>
                    <div className="text-sm text-secondary-600 mb-2">
                      Found {addonGroups.length} vendor addon groups
                    </div>
                    {addonGroups.map((group) => (
                      <div key={group.id} className="flex items-center justify-between p-3 border rounded hover:bg-secondary-50">
                        <div>
                          <h4 className="font-medium">{group.name}</h4>
                          <p className="text-sm text-secondary-600">{group.description || 'No description'}</p>
                          <p className="text-xs text-secondary-500">
                            ID: {group.id} | Required: {group.is_required ? 'Yes' : 'No'} | 
                            Min: {group.min_quantity} | Max: {group.max_quantity || 'Unlimited'}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedGroup(group.id);
                            // Scroll to mapping controls
                            setTimeout(() => {
                              const mappingControls = document.querySelector('[data-mapping-controls]');
                              if (mappingControls) {
                                mappingControls.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }
                            }, 100);
                          }}
                          className={`p-2 rounded transition-colors ${
                            selectedGroup === group.id 
                              ? 'text-green-600 bg-green-100 hover:bg-green-200' 
                              : 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100'
                          }`}
                          title={`Map "${group.name}" to this menu item`}
                        >
                          {selectedGroup === group.id ? (
                            <>✓</>
                          ) : (
                            <Link className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="p-4 text-center text-secondary-500">
                    <p>📝 No vendor addon groups available</p>
                    <p className="text-sm mt-1">Create addon groups first to map them to menu items</p>
                  </div>
                )}
              </div>
            </div>

            {/* Mapped Addon Groups */}
            <div data-mapped-groups>
              <h3 className="text-lg font-semibold mb-4">Mapped Addon Groups</h3>
              <div className="space-y-2">
                {Array.isArray(itemAddonGroups) && itemAddonGroups.length > 0 ? (
                  itemAddonGroups.map((group) => (
                    <div key={group.id} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <h4 className="font-medium">{group.name}</h4>
                        <p className="text-sm text-secondary-600">{group.description}</p>
                        <p className="text-xs text-secondary-500">Required: {group.is_required ? 'Yes' : 'No'}</p>
                      </div>
                      <button
                        onClick={() => handleUnmapGroup(group.id)}
                        className="p-2 text-red-600 hover:text-red-800"
                      >
                        <Unlink className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-secondary-500">
                    {itemAddonGroups === undefined ? 'Loading mapped groups...' : 'No addon groups mapped to this item'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mapping Controls */}
          {selectedGroup && (
            <div 
              className="mt-6 p-4 border-2 border-green-300 rounded bg-green-50 shadow-lg" 
              data-mapping-controls
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-800">
                  📍 Map Vendor Addon Group to "{item.name}"
                </h4>
                <span className="text-sm bg-green-200 text-green-800 px-2 py-1 rounded">
                  Step 2 of 2
                </span>
              </div>
              
              <div className="mb-3 p-2 bg-white rounded border">
                <strong>Selected Vendor Group:</strong> {addonGroups?.find(g => g.id === selectedGroup)?.name || `Group ${selectedGroup}`}
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-green-700 mb-1">
                    Sort Order (Display Priority)
                  </label>
                  <input
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value))}
                    className="input-field w-full"
                    placeholder="1"
                    min="1"
                    max="99"
                  />
                  <div className="text-xs text-green-600 mt-1">
                    Lower numbers appear first (1, 2, 3...)
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleMapGroup}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                    disabled={mapAddonGroupMutation.isLoading}
                  >
                    {mapAddonGroupMutation.isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                    ) : (
                      '✓ '
                    )}
                    Map Vendor Group
                  </button>
                  <button
                    onClick={() => setSelectedGroup(null)}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (menuItemsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load menu items</h3>
          <p className="text-secondary-600 mb-4">
            {menuItemsError.response?.data?.message || 'An error occurred while loading menu items'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Menu Items</h1>
          <p className="text-secondary-600">
            Manage your restaurant's menu items and dishes
          </p>
          {menuItems && (
            <p className="text-sm text-secondary-500 mt-1">
              {menuItems.length} item{menuItems.length !== 1 ? 's' : ''} loaded
            </p>
          )}
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' : 
              connectionStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500'
            }`}></div>
            <span className="text-xs text-secondary-500">
              {connectionStatus === 'connected' ? 'Connected' : 
               connectionStatus === 'disconnected' ? 'Disconnected' : 'Checking...'}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
          disabled={isLoading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </button>
      </div>



      {/* Soft Delete Information */}
      <div className="card bg-blue-50 border-blue-200 mb-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm">ℹ️</span>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900 mb-1">Soft Delete Feature</h3>
            <p className="text-sm text-blue-700">
              When you delete a menu item, it's soft deleted (hidden but not permanently removed). 
              You can restore soft deleted items using the "Restore" button. 
              Items with 🗑️ Soft Deleted badge can be restored or permanently deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            
            <select
              value={selectedMenu}
              onChange={(e) => {
                setSelectedMenu(e.target.value);
                setSelectedCategory('');
              }}
              className="input-field"
            >
              <option value="">All Menus</option>
              {menus?.map((menu) => (
                <option key={menu.id} value={menu.id}>
                  {menu.type} Menu
                </option>
              ))}
            </select>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field"
              disabled={!selectedMenu}
            >
              <option value="">All Categories</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            
            <select
              value={filterAvailability}
              onChange={(e) => setFilterAvailability(e.target.value)}
              className="input-field"
            >
              <option value="all">All Items</option>
              <option value="available">Available Only</option>
              <option value="unavailable">Unavailable Only</option>
              <option value="deleted">🗑️ Soft Deleted Items</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field"
            >
              <option value="name">Sort by Name</option>
              <option value="price">Sort by Price</option>
              <option value="created_at">Sort by Date</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Menu Items Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div key={item.id} className="card hover:shadow-md transition-shadow">
              {/* Item Image */}
              <div className="relative h-48 bg-secondary-100 rounded-lg mb-4 overflow-hidden">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`absolute inset-0 flex items-center justify-center ${item.image_url ? 'hidden' : 'flex'}`}>
                  <ImageIcon className="h-12 w-12 text-secondary-400" />
                </div>
                
                {/* Availability Badge */}
                <div className="absolute top-2 right-2">
                  {item.deleted_at ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      🗑️ Soft Deleted
                    </span>
                  ) : (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  )}
                </div>
                
                {/* Price Badge */}
                <div className="absolute bottom-2 left-2">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    £{item.price}
                  </span>
                </div>
              </div>
              
              {/* Item Details */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-secondary-900">{item.name}</h3>
                {item.description && (
                  <p className="text-sm text-secondary-600 line-clamp-2">{item.description}</p>
                )}
                
                <div className="flex items-center justify-between text-sm text-secondary-500">
                  <span className="flex items-center">
                    <Tag className="h-3 w-3 mr-1" />
                    {item.menuCategory?.name || 'No category'}
                  </span>
                  <span className="flex items-center">
                    <Hash className="h-3 w-3 mr-1" />
                    #{item.id}
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {item.deleted_at ? (
                  <>
                    <button
                      onClick={() => restoreItemMutation.mutate(item.id)}
                      className="btn-primary text-sm"
                      disabled={restoreItemMutation.isLoading}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Restore
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to permanently delete "${item.name}"? This action cannot be undone and will remove the item from the database permanently.`)) {
                          deleteItemMutation.mutate(item.id);
                        }
                      }}
                      className="btn-secondary text-sm"
                      title="Permanently Delete (Cannot be restored)"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Permanent Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => toggleAvailabilityMutation.mutate(item.id)}
                      className={`text-sm px-3 py-2 rounded-lg border ${
                        item.is_available 
                          ? 'border-green-200 text-green-700 hover:bg-green-50' 
                          : 'border-red-200 text-red-700 hover:bg-red-50'
                      }`}
                    >
                      {item.is_available ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
                      {item.is_available ? 'Hide' : 'Show'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingItem(item);
                        setModalSelectedMenu(item.menu_id || ''); // Set modal menu for category loading
                        setModalSelectedCategory(item.category_id || ''); // Set modal category
                        // Invalidate categories query to ensure fresh data
                        queryClient.invalidateQueries(['menu-categories-modal']);
                      }}
                      className="btn-primary text-sm"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleManageAddons(item)}
                      className="btn-secondary text-sm"
                      title="Manage Addons"
                    >
                      <Group className="h-4 w-4 mr-1" />
                      Addons
                    </button>
                    <button
                      onClick={() => {
                        console.log('🔘 Soft delete button clicked for item:', item.name, 'ID:', item.id);
                        if (window.confirm(`Are you sure you want to soft delete "${item.name}"? This action can be undone by restoring the item.`)) {
                          console.log('✅ User confirmed soft deletion, calling deleteItemMutation.mutate with ID:', item.id);
                          deleteItemMutation.mutate(item.id);
                        } else {
                          console.log('❌ User cancelled soft deletion');
                        }
                      }}
                      className="btn-secondary text-sm"
                      title="Soft Delete Item (Can be restored)"
                      disabled={deleteItemMutation.isLoading}
                    >
                      {deleteItemMutation.isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                      ) : (
                        <Trash2 className="h-4 w-4 mr-1" />
                      )}
                      {deleteItemMutation.isLoading ? 'Soft Deleting...' : 'Soft Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Item</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Category</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Price</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-secondary-100 rounded-lg flex items-center justify-center mr-3">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="h-full w-full object-cover rounded-lg"
                            />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-secondary-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-secondary-900">{item.name}</p>
                          {item.description && (
                            <p className="text-sm text-secondary-600 truncate max-w-xs">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-secondary-600">
                        {item.menuCategory?.name || 'No category'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-secondary-900">£{item.price}</span>
                    </td>
                    <td className="py-3 px-4">
                      {item.deleted_at ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                          🗑️ Soft Deleted
                        </span>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {item.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        {item.deleted_at ? (
                          <>
                            <button
                              onClick={() => restoreItemMutation.mutate(item.id)}
                              className="text-green-600 hover:text-green-700"
                              title="Restore Item"
                              disabled={restoreItemMutation.isLoading}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to permanently delete "${item.name}"? This action cannot be undone and will remove the item from the database permanently.`)) {
                                  deleteItemMutation.mutate(item.id);
                                }
                              }}
                              className="text-red-600 hover:text-red-700"
                              title="Permanently Delete Item (Cannot be restored)"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => toggleAvailabilityMutation.mutate(item.id)}
                              className={`${item.is_available ? 'text-green-600' : 'text-red-600'} hover:text-gray-600`}
                              title={item.is_available ? 'Hide Item' : 'Show Item'}
                            >
                              {item.is_available ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => {
                                setEditingItem(item);
                                setModalSelectedMenu(item.menu_id || ''); // Set modal menu for category loading
                                setModalSelectedCategory(item.category_id || ''); // Set modal category
                                // Invalidate categories query to ensure fresh data
                                queryClient.invalidateQueries(['menu-categories-modal']);
                              }}
                              className="text-secondary-600 hover:text-secondary-700"
                              title="Edit Item"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                console.log('🔘 List view soft delete button clicked for item:', item.name, 'ID:', item.id);
                                if (window.confirm(`Are you sure you want to soft delete "${item.name}"? This action can be undone by restoring the item.`)) {
                                  console.log('✅ User confirmed soft deletion in list view, calling deleteItemMutation.mutate with ID:', item.id);
                                  deleteItemMutation.mutate(item.id);
                                } else {
                                  console.log('❌ User cancelled soft deletion in list view');
                                }
                              }}
                              className="text-red-600 hover:text-red-700"
                              title="Soft Delete Item (Can be restored)"
                              disabled={deleteItemMutation.isLoading}
                            >
                              {deleteItemMutation.isLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleManageAddons(item)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Manage Addons"
                            >
                              <Group className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No menu items found</h3>
          <p className="text-secondary-600 mb-4">
            {searchTerm || selectedMenu || selectedCategory || filterAvailability !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first menu item'
            }
          </p>
          {!searchTerm && !selectedMenu && !selectedCategory && filterAvailability === 'all' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Item
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <MenuItemModal onClose={() => {
          setShowCreateModal(false);
          setModalSelectedMenu(''); // Reset modal state
          setModalSelectedCategory(''); // Reset category state
          // Invalidate categories query to ensure fresh data next time
          queryClient.invalidateQueries(['menu-categories-modal']);
        }} />
      )}
      
      {editingItem && (
        <MenuItemModal item={editingItem} onClose={() => {
          setEditingItem(null);
          setModalSelectedMenu(''); // Reset modal state
          setModalSelectedCategory(''); // Reset category state
          // Invalidate categories query to ensure fresh data next time
          queryClient.invalidateQueries(['menu-categories-modal']);
        }} />
      )}

      {/* Addon Management Modal */}
      {showAddonModal && selectedItemForAddons && (
        <AddonManagementModal
          item={selectedItemForAddons}
          onClose={() => {
            setShowAddonModal(false);
            setSelectedItemForAddons(null);
          }}
        />
      )}
    </div>
  );
};

export default MenuItems; 